import type { StorageDriver } from '../puzzles/profile.js';
import type { SaveData } from '../domain/save.js';
import { validateSave } from '../domain/save.js';
import { canonicalJSON, checksumJSON } from '../domain/save-codec.js';
export const CAREER_KEYS = ['sky-harbor:career:v6:A','sky-harbor:career:v6:B'] as const;
/** Pre-v6 slots are read and converted forward, never written or deleted, so an older build's career stays intact. */
export const LEGACY_CAREER_KEYS = ['sky-harbor:career:v5:A','sky-harbor:career:v5:B'] as const;
export type SaveResult = {ok:true;save:SaveData} | {ok:false;error:string};
/** One writer owns sequence allocation; a failed slot write leaves the other intact. */
export class CareerStore {
    latest: SaveData | null = null;
    private writes: Promise<void> = Promise.resolve();
    constructor(private storage: StorageDriver) {}
    load(): Promise<{save:SaveData|null;error:string|null}> {
        const read = this.writes.then(async () => {
            const slots: SaveData[] = [];
            let failed = false, corrupt = false;
            for (const key of [...CAREER_KEYS, ...LEGACY_CAREER_KEYS]) {
                try { const raw = await this.storage.get(key), save = validateSave(raw);
                    if (save) slots.push(save);else if(raw!==undefined && raw!==null) corrupt=true;
                } catch { failed = true; }
            }
            this.latest = slots.sort((a,b)=>b.seq-a.seq)[0]??null;
            return {save:this.latest,error:failed?'Career storage could not be read.':corrupt ? (this.latest?'Recovered the previous valid career slot.':'Career slots are damaged. Starting a fresh terminal.') : null};
        });
        this.writes = read.then(()=>{});
        return read;
    }

    save(snapshot: SaveData): Promise<SaveResult> {
        // Clone before queuing so caller mutations cannot change a pending write.
        const copy = validateSave(snapshot);
        if (!copy) return Promise.resolve({ok:false,error:'Career document is invalid.'});
        let result: SaveResult = {ok:false,error:'Career could not be saved.'};
        const write = this.writes.then(async () => {
            if ((this.latest?.seq??0) >= Number.MAX_SAFE_INTEGER) {result={ok:false,error:'Career sequence limit reached.'};return;}
            // Physical slot order stays monotonic even when an import replaces the career identity.
            copy.seq = (this.latest?.seq??0)+1;
            copy.checksum = checksumJSON(copy)!;
            try {
                await this.storage.set(CAREER_KEYS[copy.seq%2],copy);
                this.latest = copy;
                result = {ok:true,save:copy};
            } catch { result = {ok:false,error:'Career could not be saved. The previous save is available.'}; }
        });
        this.writes = write;
        return write.then(()=>result);
    }
    export(): string | null { return this.latest ? canonicalJSON(this.latest) : null; }
    import(text: string): Promise<SaveResult> {
        if (new TextEncoder().encode(text).length>1024*1024) return Promise.resolve({ok:false,error:'Career document exceeds 1 MB.'});
        let raw: unknown;
        try { raw = JSON.parse(text); } catch { return Promise.resolve({ok:false,error:'Career document is not JSON.'}); }
        const save = validateSave(raw);
        return save ? this.save(save) : Promise.resolve({ok:false,error:'Career document is invalid or belongs to another build.'});
    }
}
